import React, { useEffect, useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import CustomiseIcon from '@assets/icons/magicpen.svg';
import { useDispatch } from 'react-redux';
import { CustomerActionType } from '@store/branches/customer/enums';
import { toast } from '@utilities/toasts';
import { useAppSelector } from '@store/selectors';
import RemarkHistory from '@components/RemarkHistory';

interface AddRemarkModalProps {
	areaName: string;
	onCancel: () => void;
	initialRemark?: string;
	ticketId: string;
	showHistory?: boolean;
	onSuccess?: () => void;
}

const AddRemarkModal: React.FC<AddRemarkModalProps> = ({
	areaName,
	onCancel,
	initialRemark = '',
	ticketId,
	showHistory = true,
	onSuccess
}) => {
	const dispatch = useDispatch();
	const { chatHistory } = useAppSelector(state => state.customer);

	const [addedRemark, setRemark] = useState(initialRemark);

	const fetchChat = () => {
		dispatch({
			type: CustomerActionType.FETCH_TICKET_CHAT,
			payload: {
				ticketId,
				errorCB: err => {
					toast('ERROR', err);
				}
			}
		});
	};

	useEffect(() => {
		if (ticketId && showHistory) fetchChat();
	}, [ticketId, showHistory]);

	const sendRemark = remark => {
		dispatch({
			type: CustomerActionType.SEND_CHAT_MESSAGE,
			payload: {
				values: {
					chatRole: 'ERP',
					message: remark
				},
				ticketId,
				successCB: () => {
					setRemark('');
					onSuccess?.();
				},
				errorCB: err => {
					toast('ERROR', err);
				},
				finalCB: fetchChat
			}
		});
	};

	const sortedHistory = [...chatHistory].sort((a, b) => a.timestamp - b.timestamp);

	return (
		<div className="flex flex-col gap-4 p-1 w-full mt-8">
			{showHistory && (
				<>
					<div className="flex gap-3">
						<div className="border border-[var(--color-border)] rounded-lg p-4">
							<img src={CustomiseIcon} alt="Customise" className="h-7 dark:invert" />
						</div>
						<div>
							<h3 className="font-semibold text-lg ">Remarks for {areaName}</h3>
							<h2>Add, edit or review your notes for this area </h2>
						</div>
					</div>

					<span className="font-bold text-lg !gap-1">Write Remark</span>
				</>
			)}
			<InputTextarea
				autoFocus
				rows={2}
				className="w-full"
				value={addedRemark}
				onChange={e => setRemark(e.target.value)}
				placeholder="Write your reply here..."
				pt={{
					root: {
						className: 'w-full rounded-lg resize-none mt-1',
						style: { height: '60px' }
					}
				}}
			/>
			{showHistory && chatHistory.length > 0 && <RemarkHistory messages={sortedHistory} />}

			<div className="flex justify-end gap-2 mt-4">
				<Button
					className="border bg-transparent text-[var(--color-text-secondary)] border-[var(--color-border)] hover:bg-[var(--color-surface)] rounded-lg"
					label="Cancel"
					severity="secondary"
					onClick={onCancel}
				/>
				<Button
					label="Send"
					className="bg-accent text-skin-inverted hover:bg-accent-dark rounded-xl"
					onClick={() => sendRemark(addedRemark)}
				/>
			</div>
		</div>
	);
};

export default AddRemarkModal;
